/** @module ChangeTracking/ChangeLogTable */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"underscore",
	"csi/access/crews",
	"csi/action/Controller",
	"csi/comm/GetRequest",
	"csi/constants",
	"csi/core/Key",
	"csi/core/SpecBuilder",
	"csi/def",
	"csi/def/Heading",
	"csi/enums",
	"csi/heading/_base",
	"csi/heading/Spec",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/sessionConstants",
	"csi/state",
	"csi/state/rootStore",
	"csi/store/Nodes",
	"csi/store/Values",
	"csi/store/_ClientRequestMixin",
	"csi/table/Preferences",
	"csi/table/ToolbarFilter",
	"csi/table/Widget",
	"csi/types",
	"csi/widget/Tooltip",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/QueryProcessor",
	"ChangeTracking",
	"ChangeTracking/AttributeEntryName",
	"SiteAdmin/Config/DomainClassName",

	// other dependencies
	"./ToolbarExcelAll"
], function(declare, lang, _, crews, Controller, GetRequest, constants, Key, SpecBuilder, def, Heading, enums, csiHeading, Spec,
	html, locale, reflection, sessionConstants, state, {featureTogglesStore}, Nodes, Values, _ClientRequestMixin,
	TablePreferences, ToolbarFilter, Widget, types, Tooltip, xml, Query, QueryProcessor, ChangeTracking, AttributeEntryName, DomainClassName) {

	var _matrixPath = "Child:Attributes";

	var REQUEST_MODULE = "ChangeTracking";

	var ATTRIBUTE_ENTRY = "ChangeLogAttributeEntry";
	var LOG_ENTRY = "ChangeLogEntry";

	var ATTR_NEW = "NewValue";
	var ATTR_OLD = "OldValue";

	var COLUMN_CONTEXT_NAME = "LogEntryContext";

	var CHANGE_TIME_QUERY_KEY = xml.queryKey("ChangeTime");
	var CONTEXT_INSTANCE_CNL = "ContextInstanceCnl";
	var MODIFIED_QUERY_KEY = xml.queryKey("LogCurrentUser");
	var OBJECT_QUERY_KEY = xml.queryKey("DomainClass");
	var OPERATION_QUERY_KEY = xml.queryKey("Operation");

	var ATTR_NAME_QUERY_KEY = xml.queryKey(xml.ATTR_NAME, null, _matrixPath);
	var ATTR_NEW_QUERY_KEY = xml.queryKey(ATTR_NEW, null, _matrixPath);
	var ATTR_OLD_QUERY_KEY = xml.queryKey(ATTR_OLD, null, _matrixPath);

	var QUERY_LABELS = [MODIFIED_QUERY_KEY, ATTR_NEW_QUERY_KEY, ATTR_OLD_QUERY_KEY];

	/**
	 * Customize custom view options for change log
	 */
	var _Preferences = declare(TablePreferences, {
		constructor: function() {
			this.removeCustomOption("attributeKeys");
			this.removeCustomOption("chartHeight");
			this.removeCustomOption("chartHideTable");
			this.removeCustomOption("chartSettings");
			this.removeCustomOption("filterRemember");
			this.removeCustomOption("groupSettings");
			this.removeCustomOption("hiddenKeys");
			this.removeCustomOption("printComments");
			this.removeCustomOption("printHeader");
			this.removeCustomOption("printOrientation");
			this.removeCustomOption("printPaper");
			this.removeCustomOption("reportSettings");
			this.removeCustomOption("showFileSizes");
			this.removeCustomOption("sortSettings");
			this.removeCustomOption("tableHideRoot");
			this.removeCustomOption("tableImageHeight");
			this.removeCustomOption("showCountIfSuspended");
			this.removeCustomOption("tableSuspend");

			this.addCustomOption("query", {});
			this.addCustomOption("queryForServer", {});
			this.addCustomOption("showDetails", false);
		}
	});

	/**
	 * Values store for querying domain class and attributes should not apply ad-hoc filters.
	 */
	var _DomainValues = declare(Values, {
		_getServerQuery: function(query) {
			return null;
		},

		/**
		 * @override
		 *
		 * C8-21202: for SpecificationDataSheetItem show Value attribute instead of 6 attributes that start with Value in the Attribute name drop-down
		 */
		_setData: function(items) {
			if (this.spec && this.spec.format == ChangeTracking.VALUE_ATTR_CLASS) {
				var count = items.length;
				items = _.filter(items, function(item) {
					return !ChangeTracking.VALUE_ATTRS.includes(item._value);
				});

				if (count != items.length) {
					var valueItem = {
						_count: 1,
						_refs: null,
						_text: locale.resource(ChangeTracking.VALUE_ATTR) || ChangeTracking.VALUE_ATTR,
						_value: ChangeTracking.VALUE_ATTR
					};

					items.push(valueItem);
				}

			}

			this.inherited(arguments);
		}
	});

	/**
	 * Values store for change log values uses special operation.
	 */
	var _Values = declare(Values, {
		bAutoComplete: true,
		requestModule: REQUEST_MODULE,
		requestOperation: "ChangeLogValuesByXML"
	});

	/**
	 * Nodes store for change log nodes uses special operation.
	 */
	var _AdminStore = declare(Nodes, {
		chartClass: null,
		requestModule: REQUEST_MODULE,
		requestOperation: "ChangeLogQueryByXML",
		requestNodeModule: null,
		requestOmitOptions: ["direction", "noActionData", "queryOptions", "sort", "store"],
		requestOperationCount: null,
		valuesClass: _Values,
		valuePairsClass: null,
		
		/**
		 * Tracking all results does not work here as DomainInstance filtering is different than normal (see EntryRef).
		 * This should not cause any issues as the log will normally be too large to get all results anyways.
		 */
		bTrackAllResults: false,
		/**
		 * Remove change log nodes from cache as their URLs are not consistent each time store is queried
		 */
		_unload: function() {
			var cache = this.cachingStore;

			cache.query({"$Type": LOG_ENTRY}).forEach(function(node) {
				cache.remove(node.$URL);
			});

			cache.query({"$Type": ATTRIBUTE_ENTRY}).forEach(function(node) {
				cache.remove(node.$URL);
			});

			this.inherited(arguments);
		}
	});

	var _Store = declare(_AdminStore, {
		requestOperation: "ChangeLogCNLsQueryByXML",
		// requestNodeModule and requestNodeOperation are used when pressing on More button
		requestNodeModule: "ChangeTracking",
		requestNodeOperation: "ChangeLogQueryByCNLs",
		requestOmitOptions: ["direction", "noActionData", "queryOptions", "sort", "store", "start", "count"],

		getValuesStore: function(/* Spec */spec, /* Object? */storeOptions) {
			var store = this.inherited(arguments);
			store.requestParameters["Qry.Limit.Path"] = store.sourceStore.resultUrls;
			return store;
		}
	});

	/*
	 * Store for C8Object filter
	 */
	var _C8ObjStore = declare([Values, _ClientRequestMixin], {
		_resolveData: function(dfd) {
			new GetRequest(REQUEST_MODULE, "GetEnabledChildConfigurations", {
				DomainClass: this.domainClass, // String, name of domain class
				Depth: 4 // int, depth of child nodes to search, server side default is 4
			}).go().then(function(res) {
				dfd.resolve(_.map(res.getReturnValue(), function(item) {
					return {
						_text: locale.type(item),
						_value: item
					};
				}));
			});
		}
	});


	/**
	 * Set default attribute for toolbar filter to C8 Object
	 */
	var _ToolbarFilter = declare(ToolbarFilter, {
		defaultAttribute: OBJECT_QUERY_KEY
	});

	/**
	 * @property {string} _CTRVersion - Change Log Tracking version
	 */
	var _CTRVersion = "";

	/** @property {boolean} bVersionPriorTo4 - indicates whether change logging version is prior to 4 (release 3) */
	/** Request that checks access to change log and whether to show Export Excel All depends on version (only available from version 4 of change log (release 3)) **/
	var _bVersionPriorTo4 = true;

	var _ExportToExcelController = declare(Controller, {
		bShowWait: true,

		publish: function() {

			var store = this.widget.get("store");
			var filename = store.requestParameters.UI_View === "ChangeLog|_CS_Site" ? "ChangeLog.xlsx" : "Archived Change Logs.xlsx"
			var request = store.getReportRequest();
			request.addParameter("Module", "ChangeTracking");
			request.addParameter("Operation", "ChangeLogExportToExcel");

			request.addParameter("TimeZone", Intl.DateTimeFormat().resolvedOptions().timeZone);
			request.addParameter("ShowDetails", this.widget.get("showDetails"));

			var ioArgs = {
				headers: {},
				handleAs: "blob"
			};

			this.waitForPublish(request.post(ioArgs).then(function(response) {
				html.download(filename, response.data);
			}), true);

		}

	});

	return declare(Widget, {
		allowAutomaticHeight: true,
		bForceBrowse: true, // force to show links to the same page (user associate no link with deleted instance)
		enableCustomViewDialog: false,
		filterPosition: constants.FILTER_POSITION_TOOLBAR,
		prefClass: _Preferences,
		printHeader: true,

		plugins: ['print', {name: 'Export', showLabel: false, icon: "csiActionIcon iconExport", children: ['tableExcel', 'tableExcelFlat', 'tableExcelAll', 'pdf']}, 'filter', 'refresh', '|', 'pageSize'],
		pluginClasses: {
			filter: _ToolbarFilter
		},

		_actionControllers: {
			TableExcelAll: _ExportToExcelController
		},

		/** @property {string} changeLogInstanceType - if on instance, then indicates type of instance */
		changeLogInstanceType: null,

		/** @property {Tooltip[]} tooltips - Array of Tooltip objects created on view */
		tooltips: null,

		/** @property {Object} _queryValueLabels - text for values used in ad-hoc filters */
		_queryValueLabels: null,

		constructor: function(params) {
			this._queryValueLabels = {};
			this.tooltips = [];
		},

		destroy: function() {
			this._destroyTooltips();
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Before rendering find out the version of change log;
		 * Request that checks access to change log and whether to show Export Excel All depends on version (only available from version 4 of change log (release 3))
		 */
		onViewStart: function(/* Deferred[] */dfds) {
			var dfd;
			if (!_CTRVersion) {
				dfd = new GetRequest(REQUEST_MODULE, "ChangeLogGetVersion").go().then(function(res) {
					_CTRVersion = res.getReturnValue();
					var changeLogVersionNumber = _CTRVersion.split("Version ")[1];
					_bVersionPriorTo4 = /^[1-3]/.test(changeLogVersionNumber);
				});
			}
			dfds.push(dfd);
			this.inherited(arguments);
		},

		/**
		 * Show Change Log Version on the main app footer
		 */
		onViewStartBuild: function() {
			if (_CTRVersion) {
				this.set("messageRight", _CTRVersion);
			}

			if (_bVersionPriorTo4) {
				// whether to show Export Excel All depends on version (only available from version 4 of change log (release 3))
				this.toolbarNode.removePlugins(["tableExcelAll"]);
			}

			this.inherited(arguments);
		},

		_createTooltips: function() {
			var tooltips = [];
			this.tooltips.forEach(function(tooltipData) {
				if (!tooltipData.destroy) { // tooltip is not created
					tooltips.push(new Tooltip(tooltipData));
				} else { // existing tooltip
					tooltips.push(tooltipData);
				}
			});
			this.tooltips = tooltips;
		},

		_destroyTooltips: function() {
			this.tooltips.forEach(function(ttip) {
				if (ttip.destroy) {
					ttip.destroy(); // destroy tooltip's data
				}
			});
			this.tooltips = [];
		},

		/**
		 * Disable sorting in change log due to its potential size. It will by default return latest results first.
		 * @return {boolean}
		 */
		canSort: function() {
			return false;
		},

		/**
		 * Since this view only exports what is currently in view (i.e. bExportNoMore), this function should
		 * check the max export size against the display count rather than total count.
		 */
		doesQueryTotalExceedMax: function() {
			const max = sessionConstants.siteMaxExportSize;
			const total = this.queryDisplayCount;
			return (total == null) || (max && (total > max));
		},

		/**
		 * @override
		 * Apply query to default view to filter changes in past month
		 */
		onCreateDefaultCustomView(cv) {
			this.inherited(arguments);

			var q = {};
			q[CHANGE_TIME_QUERY_KEY] = types.updateDateRange(types.getDateRange(0, 0, types.DateRangeSelection.PAST_MONTH));
			cv.setValue("query", q);
			cv.setValue("queryForServer", q);
		},

		/**
		 * Only print what is currently shown by setting querySize to current number of results shown
		 * @return {Object}
		 */
		getPrintProperties: function() {
			var obj = this.inherited(arguments);
			obj.querySizeFixed = false;
			obj.querySize = this.queryDisplayCount;
			return obj;
		},

		/**
		 * Return text for ad-hoc query value
		 * @param {string} id - query filter key
		 * @param {Any} value - query filter value
		 * @return {string}
		 */
		getSpecValueLabel: function(id, value) {
			// check value against stored map since framework does not know which type of attribute value is for
			if (_.contains(QUERY_LABELS, id)) {
				var label = this._queryValueLabels[value];
				if (label != null) {
					return label;
				}
			}

			return this.inherited(arguments);
		},

		/**
		 * Return appropriate store for attribute based on current query
		 * @param {string} id - spec id
		 * @param {Object} storeOptions
		 * @return {Store}
		 */
		getSpecValues: function(id, storeOptions) {
			var spec = this.getSpec(id);
			var store;

			storeOptions = {spec: spec};

			switch (id) {

				case MODIFIED_QUERY_KEY: {
					// show list of all users
					var q = new Query().Type(xml.TYPE_USER);
					q.Or()
						.Attr("UserType", xml.OP_NOT_EQUALS, "UserType:CentricService")
						.Node(xml.PROP_URL, xml.OP_EQUALS, "centric://REFLECTION/INSTANCE/User/System");
					store = new Values({
						filterQuery: q,
						requestParameters: {
							"Qry.Value.Attr": xml.ATTR_NAME
						}
					});
					break;
				}

				case OBJECT_QUERY_KEY: {
					// show list of objects configured for change tracking
					store = this.changeLogInstanceType ? new _C8ObjStore({domainClass: this.changeLogInstanceType}) : this._getClassStore(storeOptions);
					break;
				}

				case OPERATION_QUERY_KEY: {
					// show enum store for operation types
					store = Values.createFromMemoryStore(enums.getStore(spec.format), storeOptions);	
					
					 //Remove Archive operation if SeasonArchive flag is not enabled				
					if (!featureTogglesStore.ENABLE_ARCHIVING){
						store._store.data = store._store.data.filter(item => item._text !== 'Archived');
					}
					break;
				}

				case ATTR_NAME_QUERY_KEY: {
					// show list of attributes configured for change tracking based on currently selected C8 Object
					store = this._getAttributeStore(storeOptions);
					break;
				}

				case ATTR_NEW_QUERY_KEY:
				case ATTR_OLD_QUERY_KEY: {
					// store depends on current attribute selected
					store = this._getAttributeValueStore(storeOptions);
					break;
				}

				default: break;
			}

			return store || this.inherited(arguments);
		},

		onCreateQueryStore: function(extraSpecs) {
			var spec = this.getHeadingSpec("___" + COLUMN_CONTEXT_NAME + "::0");
			if (spec) {
				extraSpecs.push(spec);
			}

			if (this.get("showDetails")) {
				var contextCodeSpec = new Spec({
					name: locale.resource("ContextCode"),
					queryKey: xml.queryKey("ContextCode"),
					querySpec: {
						id: "ContextCode",
						type: xml.ATTR_STRING,
						ren: csiHeading.getRenderer(xml.ATTR_STRING),
						ops: csiHeading.QUERY_OPS[xml.ATTR_STRING]
					}
				});
				extraSpecs.push(contextCodeSpec);
			}
		},

		/**
		 * Restrict selection of Attribute Name when C8 Object is not selected, and Attribute values when Attribute Name is not selected
		 * @param {string} id - spec id
		 * @return {Object} parameters for filter widget
		 */
		getSpecValuesWidgetParams: function(id) {
			var params = this.inherited(arguments);
			if (params) {
				var s;

				if ((id == ATTR_NAME_QUERY_KEY) && !this.hasSingleQueryValue(OBJECT_QUERY_KEY)) {
					s = "ChangeLogSelectOneClass";
				} else if (((id == ATTR_NEW_QUERY_KEY) || (id == ATTR_OLD_QUERY_KEY)) && !this.hasSingleQueryValue(ATTR_NAME_QUERY_KEY)) {
					s = "ChangeLogSelectOneAttribute";
				} else if (id == CHANGE_TIME_QUERY_KEY) {
					params.popupParams.allowBlanks = false;
				} else if (id == MODIFIED_QUERY_KEY) {
					params.query = {};
				}

				params.defaultDisplayValue = locale.resource(s || "Select");
				params.disabled = !!s;

				if (s) {
					// reset store if necessary dependencies are not met
					params.store = null;
				} else if (params.store instanceof _Values) {
					// match from beginning of string, not anywhere inside the string
					params.queryExpr = "${0}*";

					// set appropriate search delay for columns that go to server for values
					params.searchDelay = 250;
				}
			}

			return params;
		},

		/**
		 * Restrict Created/Modified attributes from appearing in custom views
		 */
		postMixInProperties: function() {
			this.watch("query", lang.hitch(this, this._onWatchQuery));
			this.inherited(arguments);

			// _QueryMixin._resolveView() will force create sortSettings if we specify sort in module.xml
			// We want our table to be sorted by change time (so we specify sort in ChangeTracking.xml),
			// but we don't want to show sort headers and allow user to change sort order
			this.sortSettings = null;

			if (this.customViewMigrationContainerKey) {
				const parts = def.decomposeViewKey(this.customViewMigrationContainerKey);
				this.changeLogInstanceType = parts.context;
			} else if (this.urlRoot != reflection.URL_SITE) {
				this.changeLogInstanceType = this.getViewNodeType();
			}

			// for Admin Site always create shared custom views
			this.prefSiteOnly = !this.changeLogInstanceType;
		},

		_refreshWidget: function() {
			this._destroyTooltips();
			this.inherited(arguments);
		},

		redrawData: function() {
			this._destroyTooltips();
			this.inherited(arguments);
		},

		getViewPreferenceKey: function() {
			return this.defView.getKey(this.changeLogInstanceType);
		},

		/**
		 * @override
		 *
		 * C8-21202: for SpecificationDataSheetItem show only Value attribute in the Attribute name drop-down
		 */
		getQueryResultQuery: function(qr) {
			qr = this.inherited(arguments);

			if (!qr)
				return qr;

			var domainClassQuery = qr[OBJECT_QUERY_KEY];

			if (domainClassQuery && domainClassQuery.length == 1 && domainClassQuery[0] == ChangeTracking.VALUE_ATTR_CLASS) {
				var attrNameQuery = qr[ATTR_NAME_QUERY_KEY];

				if (!attrNameQuery)
					return qr;

				var count = attrNameQuery.length;

				attrNameQuery = _.filter(attrNameQuery, function(attrName) {
					return attrName != ChangeTracking.VALUE_ATTR;
				});

				if (count != attrNameQuery.length) {
					qr[ATTR_NAME_QUERY_KEY] = attrNameQuery.concat(ChangeTracking.VALUE_ATTRS);
				}
			}

			return qr;
		},

		/**
		 * Track labels for query values so they are known when view is refreshed; this is necessary due to the custom way the view is filtered,
		 * such as using CNLs for Modified By, and because attribute new and old values vary by type
		 */
		setQueryValue: function(id, value) {
			var b = this.inherited(arguments);

			if (b && _.contains(QUERY_LABELS, id)) {
				var valuesStore = this._queryValueStores[id];

				if (valuesStore) {

					if (!_.isArray(value)) {
						value = [value];
					}

					_.each(value, function(v) {
						var item = valuesStore.get(v);
						if (item) {
							var label = item[valuesStore.labelProperty];
							if (label != v) {
								this._queryValueLabels[v] = label;
							}
						}
					}, this);
				}
			}

			// recreate attribute store when domain class changes
			if (b && (id == OBJECT_QUERY_KEY)) {
				delete this._queryValueStores[ATTR_NAME_QUERY_KEY];
			}

			return b;
		},

		/**
		 * Apply custom data store to query Change Tracking service to main query level
		 * @param {QueryLevel} ql
		 */
		onCreateMainQueryLevel: function(ql) {
			// it is not possible (or desirable) to query for rest of results as it could be basically infinite,
			// so just export what is currently displayed
			ql.bExportNoMore = true;

			ql.createCustomStore = lang.hitch(this, function() {
				var q = new Query().setType(LOG_ENTRY);

				if (this.changeLogInstanceType) {
					q.RefAttr(CONTEXT_INSTANCE_CNL, xml.OP_EQUALS, this.urlRoot);
				}

				if (ql._rel && ql._rel.m_filters) {
					q.importElements(ql._rel.m_filters);
					QueryProcessor.evaluateQueryExpressions(q, this.getViewNode(), this.cacheView);
				}

				var storeArgs = {
					cachingStore: this.cacheView,
					filterQuery: q,
					querySpecStore: this.get("querySpecStore"),
					viewContext: this.getContext()
				};


				// Request that checks access to change log records depends on version (only available from version 4 of change log (release 3))
				// do not check access to change log records if system admin user
				var store = state.get("admin") || crews.isInRoles(["Change Log Administrator"], this.getContext().getContextNode()) || _bVersionPriorTo4
							? new _AdminStore(storeArgs)
							: new _Store(storeArgs);

				//Pass HParam archiveQuery to @HOperation("ChangeLogQueryByXML")
				store.requestParameters = this.defView.m_id === "ChangeLog"? {"archiveQuery" : false} : {"archiveQuery": true};

				return store;

			});
		},

		/**
		 * Return store of attributes configured for change tracking for currently selected C8 object
		 * @param {Object} storeOptions
		 * @return {Values}
		 */
		_getAttributeStore: function(storeOptions) {
			const dc = this._getQueryDomainClass();
			if (dc) {
				const type = "DomainAttributeConfiguration";
				const specBuilder = new SpecBuilder(type);
				const h = new Heading();
				h.m_id = xml.ATTR_NAME;
				h.m_format = dc.type;
				h.classRenderer = AttributeEntryName;

				storeOptions.spec = specBuilder.getSpecForHeading(h);
				storeOptions.filterQuery = new Query()
					.Type(type)
					.Attr("ChangeTrackingEnabled", xml.OP_EQUALS, true)
					.Attr(xml.ATTR_NAME, xml.OP_EQUALS, dc.type, reflection.PATH_PARENT);

				return new _DomainValues(storeOptions);
			}

			return null;
		},

		/**
		 * Return store of available attribute values for certain types; others will just return empty store
		 * @param {Object} storeOptions
		 * @return {Values}
		 */
		_getAttributeValueStore: function(storeOptions) {
			var da = this._getQueryDomainAttribute();
			if (da) {
				var type = da.rca.getType();

				if (type == xml.ATTR_BOOLEAN) {
					// present true/false as only options
					return Values.createFromBooleans(storeOptions);
				} else if (type == xml.ATTR_ENUM) {
					// present all possible enum values based on attribute's enum list
					return Values.createFromMemoryStore(enums.getStore(da.rca.getFormat()), storeOptions);
				}
			}

			return null;
		},

		/**
		 * Return store of classes configured for change tracking
		 * @param {Object} storeOptions
		 * @return {Values}
		 */
		_getClassStore: function(storeOptions) {
			const type = "DomainClassConfiguration";
			const specBuilder = new SpecBuilder(type);
			const h = new Heading();
			h.m_id = xml.ATTR_NAME;
			h.m_format = types.FORMAT_TYPE;
			h.classRenderer = DomainClassName;

			storeOptions.spec = specBuilder.getSpecForHeading(h);
			storeOptions.filterQuery = new Query().Type(type).Attr("ChangeTrackingEnabled", xml.OP_EQUALS, true);

			return new _DomainValues(storeOptions);
		},

		/**
		 * Return single domain attribute referenced in ad-hoc query
		 */
		_getQueryDomainAttribute: function() {
			var value = this.getQueryValue(ATTR_NAME_QUERY_KEY);
			if (value && value.length == 1) {
				return this._getQueryDomainClass().getDomainAttribute(value[0]);
			}

			return null;
		},

		/**
		 * Return single domain class referenced in ad-hoc query
		 */
		_getQueryDomainClass: function() {
			var value = this.getQueryValue(OBJECT_QUERY_KEY);
			if (value && value.length == 1) {
				return reflection.getDomainClass(value);
			}

			return null;
		},

		_postView: function() {
			this.inherited(arguments);

			this._createTooltips();
		},

		_load: function(/* Object */func /* , ... */) {
			var dfd = this.inherited(arguments);
			dfd.then(lang.hitch(this, this._createTooltips)); // check if we need to create tooltips, required for "load more" situation
			return dfd;
		},

		/**
		 * Reset filters on attribute name and value if conditions are not met
		 */
		_onWatchQuery: function(name, oldValue, newValue) {
			if (_.isEmpty(newValue)) {
				return;
			}

			var self = this;
			var bObject = this.hasSingleQueryValue(OBJECT_QUERY_KEY);
			var bAttribute = bObject && this.hasSingleQueryValue(ATTR_NAME_QUERY_KEY);
			var q;

			function check(id, b) {
				if (self.hasQueryValue(id) && !b) {
					q = q || _.clone(newValue);
					delete q[id];
				}
			}

			check(ATTR_NAME_QUERY_KEY, bObject);
			check(ATTR_NEW_QUERY_KEY, bAttribute);
			check(ATTR_OLD_QUERY_KEY, bAttribute);

			if (q) {
				this.set("query", q);
			}
		},

		/**
		 * @override
		 * Hide or show the combined Context column and show or hide individual context columns
		 */
		onViewResolveKeysToSkip(skipSet) {
			if (this.showDetails) {
				skipSet.add(Key.untypedKeyFromParts({name: COLUMN_CONTEXT_NAME}));
			} else {
				skipSet.add(Key.untypedKeyFromParts({name: "LogEntryContext1"}));
				skipSet.add(Key.untypedKeyFromParts({name: "LogEntryContext1Code"}));
				skipSet.add(Key.untypedKeyFromParts({name: "LogEntryContext2"}));
				skipSet.add(Key.untypedKeyFromParts({name: "LogEntryContext2Code"}));
				skipSet.add(Key.untypedKeyFromParts({name: "LogEntryContext3"}));
				skipSet.add(Key.untypedKeyFromParts({name: "LogEntryContext3Code"}));
				skipSet.add(Key.untypedKeyFromParts({name: "LogEntryContext4"}));
				skipSet.add(Key.untypedKeyFromParts({name: "LogEntryContext4Code"}));
			}
		},
	});
});
